import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decryption } from '../../common/enc_dec';
import { downloadCertificate, startTest } from '../../api/apiHandler';
import { Loading } from '../../components/loading/loading';
import { formatDuration, formatDurationTest } from '../../common/utilityFunctions';
import { getLocalStorageItem, setLocalStorageItem } from '../../common/LocalStorageService';
import moment from 'moment';
import CompleteTestModal from '../../modals/completeTestModal/completeTestModal';
import { showErrorMessage } from '../../common/notification';
import { useTranslation } from 'react-i18next';
import '../../i18';

const StartTest = () => {
    const { t } = useTranslation();
    const params = useParams();
    const { course_id, course_name } = decryption(params.id);
    const [isLoading, setIsLoading] = useState(true);
    const [testDetails, setTestDetails] = useState([]);
    const [isChecked, setIsChecked] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [attempted, setAttempted] = useState([]);
    const [skipped, setSkipped] = useState(0);
    const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
    const [selectedOption, setSelectedOption] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [timer, setTimer] = useState(() => {
        const storedTimer = getLocalStorageItem("testStartingTimer");
        return storedTimer ? moment(storedTimer) : null;
    });
    const [timeLeft, setTimeLeft] = useState(null);
    const [isCompleteTestModalOpen, setIsCompleteTestModalOpen] = useState(false);
    const [isReviewingSkipped, setIsReviewingSkipped] = useState(false);
    const [skippedQuestions, setSkippedQuestions] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [isTestSubmitted, setIsTestSubmitted] = useState(false);
    const timerRef = useRef(null);
    const [certificate, setCertificate] = useState([]);
    const [noDataFound, setNoDataFound] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                await getTestList();
            } catch (error) {
                console.error('Failed to fetch test data:', error);
            }
            setIsLoading(false);
        };

        fetchData();
    }, []);

    const getTestList = async () => {
        const response = await startTest({ course_id });
        if (response?.code === 404 ||
            (response?.data?.count === 0 && response?.data?.rows?.length === 0) ||
            !response?.data?.test_questions?.length) {
            setNoDataFound(true);
            setTestDetails([]);
            setQuestions([]);
            return;
        }

        if (response?.code === 200 && response?.data?.test_questions?.length > 0) {
            setTestDetails(response?.data);
            setQuestions(response?.data?.test_questions);
            setNoDataFound(false);
        } else {
            showErrorMessage(t("failedToLoadTest"));
            setTestDetails([]);
            setQuestions([]);
            setNoDataFound(true);
        }
    };

    const handleStartTest = () => {
        if (isChecked) {
            const currentTime = moment();
            setLocalStorageItem("testStartingTimer", currentTime);
        }

        const [hours, minutes] = formatDurationTest(testDetails?.time_limit).split('hrs');
        const totalTimeInMinutes = parseInt(hours, 10) * 60 + parseInt(minutes, 10);
        const totalTimeInMilliseconds = totalTimeInMinutes * 60 * 1000;
        const endTime = moment().add(totalTimeInMilliseconds);

        setTimer(moment(endTime).valueOf());
        setIsActive(true);
    };

    useEffect(() => {
        timerRef.current = setInterval(() => {
            if (isTestSubmitted) {
                clearInterval(timerRef.current)
            }
            if (timer) {
                const remaining = moment.duration(timer - moment().valueOf());

                setTimeLeft(remaining);

                if (remaining.asMilliseconds() <= 0) {
                    clearInterval(timerRef.current);
                    setIsActive(false);
                    setIsCompleteTestModalOpen(true);
                }
                setIsActive(true);
            }
        }, 1000);
        localStorage.removeItem("testStartingTimer")

        return () => clearInterval(timerRef.current);
    }, [timer]);

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleNextQuestion = () => {
        if (selectedOption === 0) {
            setErrorMessage(t("selectFieldName", { field: t("Option") }));
            return;
        }

        setErrorMessage('');
        let answerObj = {
            question_id: questions[questionIndex]?.id,
            option_id: selectedOption,
        };

        let ansArray;
        const existingQuestionIndex = attempted.findIndex(
            (item) => item.question_id === answerObj.question_id
        );

        if (existingQuestionIndex > -1) {
            ansArray = attempted.map((item, index) =>
                index === existingQuestionIndex
                    ? { ...item, option_id: answerObj.option_id }
                    : item
            );
        } else {
            ansArray = [...attempted, answerObj];
        }

        const skippedCount = ansArray.reduce((count, queObj) => {
            if (queObj.option_id === 0) {
                count++;
            }
            return count;
        }, 0);
        setSkipped(skippedCount);

        if (questionIndex === questions.length - 1) {
            setIsCompleteTestModalOpen(true);
            setAttempted(ansArray);
        } else {
            setAttempted(ansArray);

            if (questionIndex < questions.length - 1) {
                setQuestionIndex((prevIndex) => prevIndex + 1);
                setSelectedOption(0);
            }
        }
    };

    const handleSkipQuestion = () => {
        setErrorMessage('');

        const answerObj = {
            question_id: questions[questionIndex]?.id,
            option_id: 0
        };

        let ansArray;
        const existingQuestionIndex = attempted.findIndex(
            (item) => item.question_id === answerObj.question_id
        );

        if (existingQuestionIndex > -1) {
            ansArray = attempted.map((item, index) =>
                index === existingQuestionIndex
                    ? { ...item, option_id: 0 }
                    : item
            );
        } else {
            ansArray = [...attempted, answerObj];
        }

        const skippedCount = ansArray.reduce((count, queObj) => {
            if (queObj.option_id === 0) {
                count++;
            }
            return count;
        }, 0);
        setSkipped(skippedCount);

        if (questionIndex === questions.length - 1) {
            setIsCompleteTestModalOpen(true);
            setAttempted(ansArray);
        } else {
            setAttempted(ansArray);

            if (questionIndex < questions.length - 1) {
                setQuestionIndex((prevIndex) => prevIndex + 1);
                setSelectedOption(0);
            }
        }
    };

    const handleOptionChange = (optionId) => {
        setSelectedOption(optionId);
    };

    const reviewSkippedQuestions = () => {
        setIsActive(true);
        const skippedQuestions = attempted.filter(item => item.option_id === 0);
        setSkippedQuestions(skippedQuestions);
        setIsCompleteTestModalOpen(false);
        setIsReviewingSkipped(true);

        const filteredSkippedQuestions = questions.filter(question =>
            skippedQuestions.some(skippedQuestion => skippedQuestion.question_id === question.id)
        );

        setQuestions(filteredSkippedQuestions);
        setQuestionIndex(0);
    };

    const handleCorrectAnsUpdate = async (correctAns) => {
        setCorrectAnswersCount(correctAns);
        setIsTestSubmitted(true);

        const certificateResponse = await downloadCertificate({
            course_id: course_id
        })

        if (certificateResponse.code === 200) {
            setCertificate(certificateResponse?.data)
        }
    };

    const handleConfirm = () => {
        setIsCompleteTestModalOpen(false);
        setIsTestSubmitted(true);
        stopTimer();
    };

    if (isLoading) {
        return <Loading />;
    }

    if (noDataFound) {
        return (
            <div className="course-single-area pd-top-120 pd-bottom-90">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 text-center">
                            <div className="CartMainBox">
                                {/* <h2 className="PageTitleHeading">Test Not Available</h2> */}
                                <div className="no-data-message mt-5 p-5">
                                    <h4 className="text-center mt-3 fw-light">{t('noTestFound')}</h4>
                                    {/* <button
                                        className="btn btn-base b-animate-3 mt-5 mb-5"
                                        onClick={() => navigate(-1)}
                                    >
                                        Go Back
                                    </button> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="course-single-area pd-top-120 pd-bottom-90">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="CartMainBox">
                                <h2 className="PageTitleHeading">{t('startYourTest')}</h2>
                                {isActive && timeLeft && (
                                    <h4>{t('timeLeft')}: {`${timeLeft.hours()}h ${timeLeft.minutes()}m ${timeLeft.seconds()}s`}</h4>
                                )}
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="row justify-content-center">
                                <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-12 col-12 RateAndReviewGride">
                                    <div className="TestLeftMainBox">
                                        <ul className="std-price-ul">
                                            <li className="std-price-li">
                                                <p className="std-left-lable">{t('fieldName', { field: t("Test") })} :</p>
                                                <p className="std-left-price">{testDetails?.name}</p>
                                            </li>
                                            <li className="std-price-li">
                                                <p className="std-left-lable">{t('fieldName', { field: t("courseHeading") })} :</p>
                                                <p className="std-left-price">{course_name}</p>
                                            </li>
                                            <li className="std-price-li">
                                                <p className="std-left-lable">{t('timeLimit')} :</p>
                                                <p className="std-left-price">{formatDuration(testDetails?.time_limit, t)}</p>
                                            </li>
                                            <li className="std-price-li">
                                                <p className="std-left-lable">{t('totalMarks')} :</p>
                                                <p className="std-left-price">{testDetails?.calculated_total_marks}</p>
                                            </li>
                                            <li className="std-price-li BorderTopOfLi">
                                                <h5 className="m-0">{t('instructions')} :</h5>
                                            </li>
                                            <li className="std-price-li">
                                                <p className="std-left-price text-start">
                                                    {t('readTheText')}
                                                </p>
                                            </li>
                                            <li className="std-price-li">
                                                <ul className="termandconditionul">
                                                    {testDetails && testDetails?.instructions?.length > 0 && testDetails?.instructions.map((instruction, index) => (
                                                        <li key={index}>
                                                            <p className="std-left-price text-start">{instruction.value}</p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                            {!isActive && (
                                                <>
                                                    <li>
                                                        <div className="Formchaeckbox">
                                                            <input
                                                                type="checkbox"
                                                                id="tandc"
                                                                checked={isChecked}
                                                                onChange={(e) => setIsChecked(e.target.checked)}
                                                            />
                                                            <label htmlFor="tandc">{t('readInstructions')}</label>
                                                        </div>
                                                        {!isChecked && (
                                                            <p className="text-danger mt-2">{t('checkInstructionBox')}</p>
                                                        )}
                                                    </li>
                                                    <li>
                                                        <button
                                                            className="btn btn-base b-animate-3 w-100 mt-4 StartYourTestLink"
                                                            onClick={handleStartTest}
                                                            disabled={questions.length === 0 || !isChecked}
                                                        >
                                                            {t('startTest')}
                                                        </button>
                                                    </li>
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                {isActive && !isTestSubmitted && (
                                    <div className={`col-xxl-8 col-xl-8 col-lg-8 col-md-12 col-sm-12 col-12 RateAndReviewGride QuestionGrideBox ${isActive ? 'active' : ''}`}>
                                        <div className="QuestionMainBox">
                                            <div className="QuestionBoxCard">
                                                <ul className="QuestionUl">
                                                    <li className="QuestionLi">
                                                        <h6>{questionIndex + 1}. {questions?.[questionIndex]?.question}</h6>
                                                    </li>
                                                </ul>
                                                {errorMessage && <p className="text-danger">{errorMessage}</p>}
                                                <div className="row">
                                                    {questions[questionIndex]?.option_details.map((option, idx) => (
                                                        <div key={idx} className="col-12 AnsGride">
                                                            <input
                                                                type="radio"
                                                                id={`ans${idx + 1}`}
                                                                name="ans"
                                                                className="InputRadio"
                                                                checked={selectedOption === option.id}
                                                                onChange={() => handleOptionChange(option.id)}
                                                            />
                                                            <label htmlFor={`ans${idx + 1}`} className="AnsLabel">
                                                                <div className="AnsBox">
                                                                    <span>{String.fromCharCode(97 + idx)}.</span>
                                                                    <span>{option.option}</span>
                                                                </div>
                                                                <img
                                                                    src={process.env.PUBLIC_URL + "/assets/img/icon/checkedRightImg.svg"}
                                                                    alt="checkedRightImg"
                                                                    className="checkedRightImg"
                                                                />
                                                            </label>
                                                        </div>
                                                    ))}
                                                    <div className="col-12 AnsGride">
                                                        <div className="AnsFooter">
                                                            <button className="btn btn-border-black b-animate-3" onClick={handleSkipQuestion}>
                                                                {t('skip')}
                                                            </button>
                                                            <button
                                                                className="btn btn-base b-animate-3"
                                                                onClick={handleNextQuestion}
                                                            >
                                                                {t('next')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {isTestSubmitted && (
                                    <div className={`col-xxl-8 col-xl-8 col-lg-8 col-md-12 col-sm-12 col-12 RateAndReviewGride QuestionGrideBox ${isActive ? 'active' : ''}`}>
                                        <div className={`ResultDeclarationMainDiv active`}>
                                            <ul className="std-price-ul ">
                                                <li className="std-price-li">
                                                    <p className="std-left-lable">{t('queTypes', { field: t("Total") })}</p>
                                                    <p className="std-left-price">{testDetails?.test_questions.length}</p>
                                                </li>
                                                <li className="std-price-li">
                                                    <p className="std-left-lable">{t('queTypes', { field: t("Attempted") })}</p>
                                                    <p className="std-left-price">{(attempted?.length > 0 ? attempted.filter(item => item.option_id !== 0).length : 0)}</p>
                                                </li>
                                                <li className="std-price-li">
                                                    <p className="std-left-lable">{t('queTypes', { field: t("Skipped") })}</p>
                                                    <p className="std-left-price">{skipped}</p>
                                                </li>
                                                <li className="std-price-li">
                                                    <p className="std-left-lable">{t('correctAns')}</p>
                                                    <p className="std-left-price">{correctAnswersCount}</p>
                                                </li>
                                                {certificate?.certificate && (
                                                    <li className="asdfga">
                                                        <a
                                                            href={certificate.certificate}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-base b-animate-3"
                                                        >
                                                            {t('downloadCertificate')}
                                                        </a>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                                {isCompleteTestModalOpen && (
                                    <CompleteTestModal
                                        isOpen={isCompleteTestModalOpen}
                                        isClose={() => setIsCompleteTestModalOpen(false)}
                                        isTestSubmit={handleConfirm}
                                        onSubmit={reviewSkippedQuestions}
                                        attempted={attempted}
                                        courseId={testDetails.course_id}
                                        testId={testDetails.id}
                                        stopTimer={stopTimer}
                                        onCorrectAnsUpdate={handleCorrectAnsUpdate}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </>
    );
};

export default StartTest;